/**
 * -----------------------------------------------------------------------------
 * User Edit Form
 * -----------------------------------------------------------------------------
 * These components are used to render the form for editing the user details.
 */

import React, { FC } from "react";

import { useTranslation } from "react-i18next";
import {
  AdminFormFieldCheckbox,
  AdminFormFieldDropdown,
  AdminFormFieldDurationInput,
  AdminFormFieldInput,
} from "../../../components/admin/formFields";
import { APP_CONFIG, TIMEZONE_OPTIONS } from "../../../constants/constants";
import {
  ACCOUNT_RECORDER_CONFIGURATION_OPTIONS,
  ACCOUNT_RECORDER_TYPE_VALUES,
  DEFAULT_RECORDER_TYPES,
  DISTANCE_UNITS_OPTIONS,
  ECONOMY_UNITS_OPTIONS,
  LATITUDE_LONGITUDE_FORMAT_OPTIONS,
  PRESSURE_UNITS_OPTIONS,
  ROUTE_LINE_THICKNESS_OPTIONS,
  SPEED_UNITS_OPTIONS,
  TEMPRATURE_UNITS_OPTIONS,
  VOLUME_UNITS_OPTIONS,
} from "../account/constants";
import { DEFAULT_OVERLAY_OPTIONS } from "./constants";
import {
  dbTimeToJsTime,
  formattedDateTime,
  formattedDateTimeBasedOnTimezone,
} from "../../../utils/dateTime";
import { useAccountTimezone } from "../../../hooks/timezone";
import { valuePreviewFilterSessionTimeout } from "../../../components/admin/formFieldDurationInput";

interface EnterpriseGeneralDetailsFormProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: (event: React.ChangeEvent<any>) => void;
  formikSetValue: (field: string, value: any, shouldValidate?: boolean) => void;
  handleBlur: (event: React.FocusEvent<any>) => void;
  formikSetTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean
  ) => void;
  userCanEdit: boolean;
  loadingData: boolean;
  apiDataFromList: any;
  eligibleChildOptions?: { label: string; value: string }[];

  onParentToggle: (e: React.ChangeEvent<HTMLInputElement>) => void;
  showParentWarning: boolean;
  onCancelParentWarning: () => void;
  onConfirmParentWarning: () => void;
}

export const EnterpriseGeneralDetailsForm: FC<
  EnterpriseGeneralDetailsFormProps
> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
  eligibleChildOptions,
  apiDataFromList,

  onParentToggle,
  showParentWarning,
  onCancelParentWarning,
  onConfirmParentWarning,
}) => {
    const { tzForDateFns } = useAccountTimezone();
    const { t } = useTranslation("translation", {
      keyPrefix: "sysadmin.enterprises.detailsPage.form",
    });
    return (
      <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
        <AdminFormFieldInput
          label={t("account_id")}
          type="text"
          id="account_id"
          name="account_id"
          value={values.account_id}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.account_id}
          error={errors.account_id}
          disabled={!userCanEdit}
          readOnly={true}
          detailsFormField={true}
        />
        <AdminFormFieldInput
          label={t("account_description")}
          type="text"
          id="account_description"
          name="account_description"
          value={values.account_description}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.account_description}
          error={errors.account_description}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
        <AdminFormFieldInput
          label={t("creation_date")}
          type="text"
          id="creation_date"
          name="creation_date"
          // value={values.creation_date}
          value={formattedDateTimeBasedOnTimezone(
            dbTimeToJsTime(parseInt(values.creation_date)),
            tzForDateFns,
            APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME // Ensure this is "MM/dd/yy HH:mm:ss"
          )}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.creation_date}
          error={errors.creation_date}
          disabled={!userCanEdit}
          readOnly={true}
          detailsFormField={true}
        />
        <AdminFormFieldInput
          label={t("last_login")}
          type="text"
          id="last_login"
          name="last_login"
          // value={values.last_login}
          value={
            values?.last_login !== "-"
              ? formattedDateTimeBasedOnTimezone(
                dbTimeToJsTime(parseInt(values.last_login)),
                tzForDateFns,
                APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME // Ensure this is "MM/dd/yy HH:mm:ss"
              )
              : values.last_login
          } //Handling Null value for last login of newly created enterprise
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.last_login}
          error={errors.last_login}
          disabled={!userCanEdit}
          readOnly={true}
          detailsFormField={true}
        />
        <AdminFormFieldInput
          label={t("password")}
          type="password"
          id="password"
          name="password"
          value={values.password}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.password}
          error={errors.password}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
        <AdminFormFieldDropdown
          loadingData={loadingData}
          label={t("timezone")}
          id="timezone"
          name="timezone"
          value={values.timezone}
          options={TIMEZONE_OPTIONS}
          onChange={(e: any) => {
            formikSetValue("timezone", e?.value);
          }}
          onBlur={(e: any) => {
            formikSetTouched("timezone", true);
            handleBlur(e);
          }}
          touched={touched.timezone}
          error={errors.timezone}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
        <div className="col-span-12 grid grid-cols-12 gap-4">
          <AdminFormFieldCheckbox
            label={t("is_active")}
            id="is_active"
            type="checkbox"
            name="is_active"
            checked={values.is_active}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.is_active}
            error={errors.is_active}
            disabled={!userCanEdit}
            customWrapperClass="col-span-6"
          />
          <AdminFormFieldCheckbox
            label={t("toggleft")}
            id="toggleft"
            type="checkbox"
            name="toggleft"
            checked={values.toggleft}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.toggleft}
            error={errors.toggleft}
            disabled={!userCanEdit}
            customWrapperClass="col-span-6"
          />
        </div>
        <AdminFormFieldInput
          label={t("contact_name")}
          type="text"
          id="contact_name"
          name="contact_name"
          value={values.contact_name}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.contact_name}
          error={errors.contact_name}
          disabled={!userCanEdit}
          detailsFormField={true}
          customWrapperClass="col-span-4"
        />
        <AdminFormFieldInput
          label={t("contact_phone")}
          type="text"
          id="contact_phone"
          name="contact_phone"
          value={values.contact_phone}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.contact_phone}
          error={errors.contact_phone}
          disabled={!userCanEdit}
          detailsFormField={true}
          customWrapperClass="col-span-4"
        />
        <AdminFormFieldInput
          label={t("contact_email")}
          type="email"
          id="contact_email"
          name="contact_email"
          value={values.contact_email}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.contact_email}
          error={errors.contact_email}
          disabled={!userCanEdit}
          detailsFormField={true}
          customWrapperClass="col-span-4"
        />
        <div className="col-span-12 grid grid-cols-12 gap-4">
          {/* NOT USED IN FT 1.0
        <AdminFormFieldCheckbox
          label={t('unlock_account')}
          id="unlock_account"
          type="checkbox"
          name="unlock_account"
          checked={values.unlock_account}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.unlock_account}
          error={errors.unlock_account}
          disabled={!userCanEdit}
          customWrapperClass="col-span-6"
        /> */}
          {/* NOT USED IN FT 1.0
        <AdminFormFieldCheckbox
          label={t('drivers_assigned_to_devices')}
          id="drivers_assigned_to_devices"
          type="checkbox"
          name="drivers_assigned_to_devices"
          checked={values.drivers_assigned_to_devices}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.drivers_assigned_to_devices}
          error={errors.drivers_assigned_to_devices}
          disabled={!userCanEdit}
          customWrapperClass="col-span-6"
        /> */}
        </div>
        <div className="col-span-12 grid grid-cols-12 gap-4">
          <AdminFormFieldDurationInput
            label={t("session_timeout")}
            id="session_timeout"
            name="session_timeout"
            value={values.session_timeout}
            valuePreviewFilter={valuePreviewFilterSessionTimeout}
            loading={loadingData}
            onChange={(totalSeconds) => {
              formikSetValue("session_timeout", totalSeconds);
            }}
            onBlur={() => {
              formikSetTouched("session_timeout", true);
            }}
            touched={touched.session_timeout}
            error={errors.session_timeout}
            disabled={!userCanEdit}
            detailsFormField={true}
            customWrapperClass="col-span-6"
          />
        </div>
        {/* Not Required */}
        {/* <div className="col-span-12 grid grid-cols-12 gap-4">
        <AdminFormFieldCheckbox
          label={t('allow_auto_add_devices')}
          id="allow_auto_add_devices"
          type="checkbox"
          name="allow_auto_add_devices"
          checked={values.allow_auto_add_devices}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.allow_auto_add_devices}
          error={errors.allow_auto_add_devices}
          disabled={!userCanEdit}
          customWrapperClass="col-span-6"
        />
      </div> */}
        {/* Not Required */}
        {/* <div className="col-span-12">
        <AdminFormFieldInput
          label={t('maximum_devices')}
          type="number"
          id="maximum_devices"
          name="maximum_devices"
          value={values.maximum_devices}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.maximum_devices}
          error={errors.maximum_devices}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      </div> */}
        {/* Not Required */}
        {/* <AdminFormFieldInput
        label={t('max_commands_locates')}
        type="number"
        id="max_commands_locates"
        name="max_commands_locates"
        value={values.max_commands_locates}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.max_commands_locates}
        error={errors.max_commands_locates}
        disabled={!userCanEdit}
        detailsFormField={true}
      /> */}
        {/* Not Required */}
        {/* <AdminFormFieldInput
        label={t('total')}
        type="number"
        id="total"
        name="total"
        value={values.total}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.total}
        error={errors.total}
        disabled={!userCanEdit}
        detailsFormField={true}
        bottomLabel={t('total_bottom_label')}
      /> */}
        <AdminFormFieldInput
          label={t("account_manager")}
          type="text"
          id="account_manager"
          name="account_manager"
          value={values.account_manager}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.account_manager}
          error={errors.account_manager}
          disabled={!userCanEdit}
          detailsFormField={true}
          customWrapperClass="col-span-12"
        />

        <div className="col-span-12 grid grid-cols-12 gap-4 items-start">
          <AdminFormFieldCheckbox
            label={t("is_parent")}
            id="is_parent"
            type="checkbox"
            name="is_parent"
            checked={values.is_parent}
            onChange={onParentToggle}
            onBlur={handleBlur}
            touched={touched.is_parent}
            error={errors.is_parent}
            disabled={!userCanEdit}
            customWrapperClass="col-span-6 mt-3"
          />

          <>
            {/* Warning message */}
            {showParentWarning && (
              <div className="col-span-12 mt-2 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-md shadow-sm">
                <p className="text-sm text-yellow-800 mb-3 font-medium">
                  This account is currently linked to a parent [{values.parent_id}
                  ]. Enabling 'Is Parent' will remove this link.
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={onConfirmParentWarning}
                    className="px-4 py-1.5 text-sm bg-yellow-500 hover:bg-yellow-600 text-white rounded font-semibold transition-colors"
                  >
                    Continue
                  </button>
                  <button
                    type="button"
                    onClick={onCancelParentWarning}
                    className="px-4 py-1.5 text-sm bg-gray-200 hover:bg-gray-300 text-gray-800 rounded font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {values.is_parent ? (
              <AdminFormFieldDropdown
                loadingData={loadingData}
                label="Child Accounts"
                id="child_accounts"
                name="child_accounts"
                value={values.child_accounts}
                options={eligibleChildOptions || []}
                isMulti={true}
                onChange={(e: any) => formikSetValue("child_accounts", e)}
                onBlur={(e: any) => {
                  formikSetTouched("child_accounts", true);
                  handleBlur(e);
                }}
                touched={touched.child_accounts}
                error={
                  touched.child_accounts &&
                    (!values.child_accounts || values.child_accounts.length === 0)
                    ? "At least 1 child account is mandatory."
                    : touched.child_accounts
                      ? errors.child_accounts
                      : ""
                }
                disabled={!userCanEdit}
                detailsFormField={true}
                customWrapperClass="col-span-6"
                dimensionClass="w-full min-h-11"
              />
            ) : apiDataFromList?.is_child_account ? (
              <AdminFormFieldInput
                label={t("parent_id")}
                type="text"
                id="parent_id"
                name="parent_id"
                value={values.parent_id}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched.parent_id}
                error={errors.parent_id}
                disabled={!userCanEdit}
                readOnly={true}
                detailsFormField={true}
                customWrapperClass="col-span-6"
              />
            ) : null}
          </>
        </div>

        <AdminFormFieldDropdown
          loadingData={loadingData}
          label={t("private_label_name")}
          id="private_label_name"
          name="private_label_name"
          value={values.private_label_name}
          options={[
            // TODO: Add actual options
            { label: "Private Label 1", value: "1" },
            { label: "Private Label 2", value: "2" },
            { label: "Private Label 3", value: "3" },
            { label: "Private Label 4", value: "4" },
          ]}
          onChange={(e) => {
            formikSetValue("private_label_name", e?.value);
          }}
          onBlur={(e) => {
            formikSetTouched("private_label_name", true);
            handleBlur(e);
          }}
          touched={touched.private_label_name}
          error={errors.private_label_name}
          disabled={!userCanEdit}
          detailsFormField={true}
          customWrapperClass="col-span-4"
        />
        {/* Not Required */}
        {/* <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t('geocoder_mode')}
        id="geocoder_mode"
        name="geocoder_mode"
        value={values.private_label_name}
        options={[
          // TODO: Add actual options
          { label: "Geocoder Mode 1", value: "1" },
          { label: "Geocoder Mode 2", value: "2" },
          { label: "Geocoder Mode 3", value: "3" },
          { label: "Geocoder Mode 4", value: "4" },
        ]}
        onChange={(e) => { formikSetValue("geocoder_mode", e?.value); }}
        onBlur={(e) => { formikSetTouched("geocoder_mode", true); handleBlur(e); }}
        touched={touched.geocoder_mode}
        error={errors.geocoder_mode}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-4"
      /> */}
        <AdminFormFieldCheckbox
          label={t("border_crossing")}
          id="border_crossing"
          type="checkbox"
          name="border_crossing"
          checked={values.border_crossing}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.border_crossing}
          error={errors.border_crossing}
          disabled={!userCanEdit}
          customWrapperClass="col-span-4 mt-3"
        />
        {/* Not Required */}
        {/* <AdminFormFieldInput
        label={t('dcs_properties_id')}
        type="text"
        id="dcs_properties_id"
        name="dcs_properties_id"
        value={values.dcs_properties_id}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.dcs_properties_id}
        error={errors.dcs_properties_id}
        disabled={!userCanEdit}
        detailsFormField={true}
      /> */}
        {/* Not Required */}
        {/* <AdminFormFieldInput
        label={t('sms_properties')}
        type="text"
        id="sms_properties"
        name="sms_properties"
        value={values.sms_properties}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.sms_properties}
        error={errors.sms_properties}
        disabled={!userCanEdit}
        detailsFormField={true}
      /> */}
        {/* CHECKBOXES START */}
        <div className="col-span-12 grid grid-cols-12 gap-4">
          {[
            //"web_services",{/* Not Required */}
            "driver_elogs",
            //"hos_data_on_maps",{/* Not Required */}
            // "27_hour_reboot_option", // NOT USED IN FT 1.0
            //"speed_behaviour_option",{/* Not Required */}
            "dtc_popups_option",
            "maint_labels",
            "pto_labels",
            // "view_video_option",
            //"sso_to_vlstracks",{/* Not Required */}
            //"left_menu_hamburger",{/* Not Required */}
            "driver_groups",
            //"snapped_route",{/* Not Required */}
            "fleet_tag",
            "panic_sos",
            "fuel_card_integration",
            "snow_plows",
            "enablecrashtrack",
            "usegeocategories",
            "assetimageonmap",
            "incidentreviewallowed",
            "incabcoachingallowed",
            "serializedziptieallowed",
            "userloginasallowed",
          ].map((checkbox_field_name, i) => (
            <AdminFormFieldCheckbox
              key={`checkbox-${i}`}
              label={t(checkbox_field_name)}
              id={checkbox_field_name}
              type="checkbox"
              name={checkbox_field_name}
              checked={values?.[checkbox_field_name]}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched?.[checkbox_field_name]}
              error={errors?.[checkbox_field_name]}
              disabled={!userCanEdit}
              customWrapperClass="col-span-6"
            />
          ))}
        </div>
        {/* CHECKBOXES END */}
        <div className="col-span-12 grid grid-cols-12 gap-4">
          <hr className="col-span-12" />
        </div>
        <div className="col-span-12 grid grid-cols-12 gap-4">
          {["view_video_option"].map((checkbox_field_name, i) => (
            <AdminFormFieldCheckbox
              key={`checkbox-${i}`}
              label={"Camera"}
              id={checkbox_field_name}
              type="checkbox"
              name={checkbox_field_name}
              checked={values?.[checkbox_field_name]}
              onChange={(e) => {
                handleChange(e);
                if (!e.target.checked) {
                  formikSetValue("recorder_type", "0");
                  formikSetValue("recorder_config_allowed", "0");
                }
                // simulated delay to display validated error / success form states correctly
                setTimeout(() => {
                  formikSetTouched("view_video_option");
                }, 200);
              }}
              onBlur={handleBlur}
              touched={touched?.[checkbox_field_name]}
              error={errors?.[checkbox_field_name]}
              disabled={!userCanEdit}
              customWrapperClass="col-span-6"
            />
          ))}
        </div>
        <div className="col-span-12 grid grid-cols-12 gap-4">
          <AdminFormFieldDropdown
            loadingData={loadingData}
            label={t("default_recorder")}
            id="recorder_type"
            name="recorder_type"
            value={parseInt(values.recorder_type) > 0 ? values.recorder_type : ""}
            options={DEFAULT_RECORDER_TYPES}
            onChange={(e) => {
              formikSetValue("recorder_type", e?.value);
              if (e?.value === ACCOUNT_RECORDER_TYPE_VALUES.SURFSIGHT) {
                formikSetValue("recorder_config_allowed", "0");
              }
              setTimeout(() => {
                formikSetTouched("recorder_config_allowed");
              }, 200);
            }}
            onBlur={(e) => {
              formikSetTouched("recorder_type", true);
              handleBlur(e);
            }}
            touched={touched.recorder_type}
            error={errors.recorder_type}
            disabled={!userCanEdit || !values.view_video_option}
            detailsFormField={true}
            customWrapperClass="col-span-6"
            key={`recorder_type_${values.view_video_option}`}
          />
          <AdminFormFieldDropdown
            loadingData={loadingData}
            label={t("recorder_configurations")}
            id="recorder_config_allowed"
            name="recorder_config_allowed"
            value={
              parseInt(values.recorder_config_allowed) > 0
                ? `${values.recorder_config_allowed}`
                : "0"
            }
            options={ACCOUNT_RECORDER_CONFIGURATION_OPTIONS}
            onChange={(e) => {
              formikSetValue("recorder_config_allowed", e?.value);
            }}
            onBlur={(e) => {
              formikSetTouched("recorder_config_allowed", true);
              handleBlur(e);
            }}
            touched={touched.recorder_config_allowed}
            error={errors.recorder_config_allowed}
            disabled={!userCanEdit || !values.view_video_option}
            detailsFormField={true}
            customWrapperClass="col-span-6"
            key={`recorder_configs_${values.view_video_option}_${values.recorder_config_allowed}`}
            readOnly={
              values.recorder_type !== ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX
            }
          />
        </div>
        <div className="col-span-12 grid grid-cols-12 gap-4">
          <hr className="col-span-12" />
        </div>
        <AdminFormFieldDropdown
          loadingData={loadingData}
          label={t("speed_units")}
          id="speed_units"
          name="speed_units"
          value={values.speed_units}
          options={SPEED_UNITS_OPTIONS}
          onChange={(e) => {
            formikSetValue("speed_units", e?.value);
          }}
          onBlur={(e) => {
            formikSetTouched("speed_units", true);
            handleBlur(e);
          }}
          touched={touched.speed_units}
          error={errors.speed_units}
          disabled={!userCanEdit}
          detailsFormField={true}
          customWrapperClass="col-span-6"
        />
        <AdminFormFieldDropdown
          loadingData={loadingData}
          label={t("distance_units")}
          id="distance_units"
          name="distance_units"
          value={values.distance_units}
          options={DISTANCE_UNITS_OPTIONS}
          onChange={(e) => {
            formikSetValue("distance_units", e?.value);
          }}
          onBlur={(e) => {
            formikSetTouched("distance_units", true);
            handleBlur(e);
          }}
          touched={touched.distance_units}
          error={errors.distance_units}
          disabled={!userCanEdit}
          detailsFormField={true}
          customWrapperClass="col-span-6"
        />
        <AdminFormFieldDropdown
          loadingData={loadingData}
          label={t("volume_units")}
          id="volume_units"
          name="volume_units"
          value={values.volume_units}
          options={VOLUME_UNITS_OPTIONS}
          onChange={(e) => {
            formikSetValue("volume_units", e?.value);
          }}
          onBlur={(e) => {
            formikSetTouched("volume_units", true);
            handleBlur(e);
          }}
          touched={touched.volume_units}
          error={errors.volume_units}
          disabled={!userCanEdit}
          detailsFormField={true}
          customWrapperClass="col-span-4"
        />
        <AdminFormFieldDropdown
          loadingData={loadingData}
          label={t("economy_units")}
          id="economy_units"
          name="economy_units"
          value={values.economy_units}
          options={ECONOMY_UNITS_OPTIONS}
          onChange={(e) => {
            formikSetValue("economy_units", e?.value);
          }}
          onBlur={(e) => {
            formikSetTouched("economy_units", true);
            handleBlur(e);
          }}
          touched={touched.economy_units}
          error={errors.economy_units}
          disabled={!userCanEdit}
          detailsFormField={true}
          customWrapperClass="col-span-4"
        />
        <AdminFormFieldDropdown
          loadingData={loadingData}
          label={t("pressure_units")}
          id="pressure_units"
          name="pressure_units"
          value={values.pressure_units}
          options={PRESSURE_UNITS_OPTIONS}
          onChange={(e) => {
            formikSetValue("pressure_units", e?.value);
          }}
          onBlur={(e) => {
            formikSetTouched("pressure_units", true);
            handleBlur(e);
          }}
          touched={touched.pressure_units}
          error={errors.pressure_units}
          disabled={!userCanEdit}
          detailsFormField={true}
          customWrapperClass="col-span-4"
        />
        <AdminFormFieldDropdown
          loadingData={loadingData}
          label={t("temperature_units")}
          id="temperature_units"
          name="temperature_units"
          value={values.temperature_units}
          options={TEMPRATURE_UNITS_OPTIONS}
          onChange={(e) => {
            formikSetValue("temperature_units", e?.value);
          }}
          onBlur={(e) => {
            formikSetTouched("temperature_units", true);
            handleBlur(e);
          }}
          touched={touched.temperature_units}
          error={errors.temperature_units}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
        <AdminFormFieldDropdown
          loadingData={loadingData}
          label={t("latitude_longitude_format")}
          id="latitude_longitude_format"
          name="latitude_longitude_format"
          value={values.latitude_longitude_format}
          options={LATITUDE_LONGITUDE_FORMAT_OPTIONS}
          onChange={(e) => {
            formikSetValue("latitude_longitude_format", e?.value);
          }}
          onBlur={(e) => {
            formikSetTouched("latitude_longitude_format", true);
            handleBlur(e);
          }}
          touched={touched.latitude_longitude_format}
          error={errors.latitude_longitude_format}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
        {/* Not Required */}
        {/* <div className="col-span-12 grid grid-cols-6">
        <AdminFormFieldInput
          label={t("route_segment_color_rule")}
          type="text"
          id="route_segment_color_rule"
          name="route_segment_color_rule"
          value={values.route_segment_color_rule}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.route_segment_color_rule}
          touched={touched.route_segment_color_rule}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      </div> */}
        <AdminFormFieldDropdown
          loadingData={loadingData}
          label={t("route_line_thickness")}
          id="route_line_thickness"
          name="route_line_thickness"
          value={values.route_line_thickness}
          options={ROUTE_LINE_THICKNESS_OPTIONS}
          onChange={(e) => {
            formikSetValue("route_line_thickness", e?.value);
          }}
          onBlur={(e) => {
            formikSetTouched("route_line_thickness", true);
            handleBlur(e);
          }}
          touched={touched.route_line_thickness}
          error={errors.route_line_thickness}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
        {/* Not Required */}
        {/* <AdminFormFieldInput
        label={t("multi_vehicle_map_name")}
        type="text"
        id="multi_vehicle_map_name"
        name="multi_vehicle_map_name"
        value={values.multi_vehicle_map_name}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.multi_vehicle_map_name}
        touched={touched.multi_vehicle_map_name}
        disabled={!userCanEdit}
        detailsFormField={true}
      /> */}
        {/* Not Required */}
        {/* <AdminFormFieldInput
        label={t("default_login_userId")}
        type="text"
        id="default_login_userId"
        name="default_login_userId"
        value={values.default_login_userId}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.default_login_userId}
        touched={touched.default_login_userId}
        disabled={!userCanEdit}
        detailsFormField={true}
      /> */}
        <AdminFormFieldDropdown
          loadingData={loadingData}
          label={t("default_overlay")}
          id="default_overlay"
          name="default_overlay"
          value={values.default_overlay}
          options={DEFAULT_OVERLAY_OPTIONS}
          onChange={(e) => {
            formikSetValue("default_overlay", e?.value);
          }}
          onBlur={(e) => {
            formikSetTouched("default_overlay", true);
            handleBlur(e);
          }}
          touched={touched.default_overlay}
          error={errors.default_overlay}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
         <AdminFormFieldInput
          label={t("minidleseconds")}
          type="number"
          id="minidleseconds"
          name="minidleseconds"
          value={values.minidleseconds}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.minidleseconds}
          error={errors.minidleseconds}
          disabled={!userCanEdit}
          detailsFormField={true}
          customWrapperClass="col-span-6"
        />
        <AdminFormFieldDurationInput
        label={t("cameraliveviewduration")}
        id="cameraliveviewduration"
        name="cameraliveviewduration"
        value={values.cameraliveviewduration}
        loading={loadingData}
        onChange={(totalSeconds) => {
          formikSetValue("cameraliveviewduration", totalSeconds);
        }}
        onBlur={() => {
          formikSetTouched("cameraliveviewduration", true);
        }}
        touched={touched.cameraliveviewduration}
        error={errors.cameraliveviewduration}
        disabled={!userCanEdit}
        detailsFormField={true}
        minutesAsInput={true}
        showSeconds={true}
        hideHours={true}
        maxMinutes={10}
      />
      </div>
    );
  };
